import {
  ActivityStatus,
  ActivityType,
  SaveGhccActivityProgressData,
  SaveNonGhccActivityProgressData,
  type Activity,
  type ActivityMeta,
} from '@/models/Activity';
import { GhccRequestSuffix } from '@/models/Ghcc';

import { activityMetaMap } from '@/lib/activityMeta';

import {
  getActivityDetailsEndpoint,
  getProgressReportEndpoint,
  getStartActivityEndpoint,
  getUpdateProgressEndpoint,
} from './endpoints';
import fetch from './fetch';
import { isAnalyticsEnabled } from './storage';

/**
 * Get activity type and icon
 * @param {number} nodeTypeId
 * @returns {ActivityIcon}
 */
export const getActivityMeta = (nodeTypeId?: number): ActivityMeta => {
  const defaultMeta: ActivityMeta = { icon: 'goodhabitz' };

  if (!nodeTypeId) {
    return defaultMeta;
  }

  return activityMetaMap[nodeTypeId] || defaultMeta;
};

export const getActivityStatusTranslationKey = (status?: ActivityStatus): string => {
  switch (status) {
    case ActivityStatus.completed:
      return 'redo';
    case ActivityStatus.incomplete:
      return 'continue';
    default:
      return 'start';
  }
};

export const getSnakeCasedTestStatusTranslationKey = (status: ActivityStatus): string => {
  return status.split(' ').join('_').toLowerCase();
};

export const getTestStatus = (score: number | undefined) => {
  if (!score || score === 0) {
    return ActivityStatus.notAttempted;
  }

  if (score >= 70) {
    return ActivityStatus.passed;
  }

  return ActivityStatus.failed;
};

export const getActivityStatus = (score: number) => {
  if (score === 0) {
    return ActivityStatus.ready;
  }

  if (score === 100) {
    return ActivityStatus.completed;
  }

  return ActivityStatus.incomplete;
};

export const getActivityDetails = async (slug: string): Promise<Activity> => {
  const response = await fetch(getActivityDetailsEndpoint(slug));

  return await response.json();
};

export const saveActivityProgress = async (
  progress: SaveGhccActivityProgressData | SaveNonGhccActivityProgressData,
) => {
  await fetch(getUpdateProgressEndpoint(), {
    method: 'POST',
    body: JSON.stringify(progress),
    headers: {
      'Content-Type': 'application/json',
    },
    keepalive: true,
  });
};

export const getExtendedActivityUrl = (
  activity: Activity,
  locale: string,
  externalPersonReference: string | undefined,
) => {
  if (!activity.activityUrl) {
    return undefined;
  }

  const extendedActivityUrl = new URL(activity.activityUrl);

  switch (activity.activityType) {
    case ActivityType.assessment:
      extendedActivityUrl.searchParams.append('origin', activity.contentType as string);
      break;
    case ActivityType.driveToSell:
      extendedActivityUrl.searchParams.append('languageRegion', locale);
      break;
    default:
      extendedActivityUrl.searchParams.append('userId', externalPersonReference ?? '');
      extendedActivityUrl.searchParams.append('analytics', isAnalyticsEnabled() ? 'true' : 'false');
      break;
  }

  if (import.meta.env.DEV && activity.activityType === ActivityType.assessment)
    return extendedActivityUrl
      .toString()
      .replace(extendedActivityUrl.origin, document.location.origin);

  return extendedActivityUrl.toString();
};

export const startActivityRequest = async (
  activityId: string,
  sessionId: string,
  source: 'ghcc' | 'gh',
) => {
  const requestData = {
    suffix: GhccRequestSuffix.StartActivity,
    moduleId: activityId,
    sessionId,
    source,
  };

  const response = await fetch(getStartActivityEndpoint(), {
    method: 'POST',
    body: JSON.stringify(requestData),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return await response.json();
};

export const progressReportRequest = async (
  progress: SaveGhccActivityProgressData | SaveNonGhccActivityProgressData,
) => {
  const response = await fetch(getProgressReportEndpoint(), {
    method: 'POST',
    body: JSON.stringify(progress),
    headers: {
      'Content-Type': 'application/json',
    },
    keepalive: true,
  });

  return await response.json();
};
